import { cfg } from 'config/runtimeConfig'
/**
 * SSO client — same mechanics as Kit19Sales (src/lib/sso/ssoClient.js):
 *
 * 1. The main Kit19 app redirects here with a plaintext JWT:
 *      /callback?token=<JWT>&deviceId=<id>&returnUrl=<path>
 * 2. The JWT is never decoded locally — it is validated server-side via
 *      POST {VITE_SSO_API_URL}Authorize/ValidateToken
 *      headers: Authorization: Bearer <token>;  body: { accessToken }
 *    success = status === 1 && details.status === 1.
 * 3. The user's details (UserId, ParentId, name, timezone, branding) are
 *    flattened and stored in localStorage under the SAME keys the Sales
 *    module uses, so the session is shared between Kit19 satellite apps.
 * 4. Business API calls keep the universal Token in the body; identity is
 *    carried as UserId/ParentId inside Details — never the JWT.
 *
 * Unlike Kit19Sales, the timezone from meta.userDetail IS persisted
 * (`Timezone` key) — the Sales module dropped it, and the interaction-history
 * endpoint needs it.
 */

export interface SsoConfig {
  enabled: boolean
  loginUrl: string
  logoutUrl: string
  apiUrl: string
  appName: string
  tokenKey: string
}

export function getSsoConfig(): SsoConfig {
  return {
    enabled: String(cfg('REACT_APP_SSO_ENABLED') ?? 'true') === 'true',
    loginUrl: cfg('REACT_APP_SSO_LOGIN_URL') ?? 'https://sso.crmapp.in.net/login',
    logoutUrl: cfg('REACT_APP_SSO_LOGOUT_URL') ?? 'https://sso.crmapp.in.net/logout',
    apiUrl: (cfg('REACT_APP_SSO_API_URL') ?? 'https://auth.kit19.com/api/').replace(/\/?$/, '/'),
    appName: cfg('REACT_APP_SSO_APP_NAME') ?? 'Kit19 Entity',
    tokenKey: 'kit19_token',
  }
}

/* ------------------------------------------------------------- URL params */

export interface SsoCallbackParams {
  token: string | null
  deviceId: string | null
  returnUrl: string | null
  error: string | null
  errorDescription: string | null
}

/** Reads the /callback parameters from the query string or hash fragment. */
export function extractCallbackParams(search: string, hash: string): SsoCallbackParams {
  const params = new URLSearchParams(search)
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash)
  const pick = (name: string) => params.get(name) || hashParams.get(name)
  return {
    token: pick('token'),
    deviceId: pick('deviceId'),
    returnUrl: pick('returnUrl'),
    error: pick('error'),
    errorDescription: pick('error_description'),
  }
}

/* ------------------------------------------------------------ validation */

export interface SsoUserData {
  userId: string
  parentId: string
  displayName: string
  loginName: string
  firstName: string
  lastName: string
  email: string
  mobile: string
  companyName: string
  timezone: string
  countryCode: string
  profilePicture: string
  logo: string
  faviconIcon: string
  domain: string
}

interface ValidateResult {
  valid: boolean
  message?: string
  userData?: SsoUserData
}

/** POST Authorize/ValidateToken — the server does the real "decrypt". */
export async function validateSsoToken(token: string): Promise<ValidateResult> {
  const config = getSsoConfig()
  try {
    const response = await fetch(`${config.apiUrl}Authorize/ValidateToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accessToken: token }),
    })
    const data = await response.json()

    if (data?.status === 1 && data?.details?.status === 1) {
      return { valid: true, userData: transformUserData(data.details) }
    }
    return { valid: false, message: data?.message || data?.details?.message || 'Token validation failed' }
  } catch (err) {
    return { valid: false, message: (err as Error).message || 'Token validation failed' }
  }
}

/** Flattens details.{userInfo, meta.userDetail, meta.teamChat, domainInfo}. */
function transformUserData(details: any): SsoUserData {
  const userInfo = details?.userInfo ?? {}
  const userDetail = details?.meta?.userDetail ?? {}
  const teamChat = details?.meta?.teamChat ?? {}
  const domainInfo = details?.domainInfo ?? {}
  const tokenInfo = details?.tokenInfo ?? {}

  return {
    userId: String(userInfo.userId ?? ''),
    parentId: String(userInfo.parentId ?? ''),
    displayName: userInfo.displayName ?? '',
    loginName: userInfo.userLogin ?? tokenInfo.username ?? '',
    firstName: userDetail.fName ?? teamChat.FName ?? '',
    lastName: userDetail.lName ?? teamChat.LName ?? '',
    email: userDetail.email ?? teamChat.EMail ?? '',
    mobile: userDetail.mobile ?? teamChat.Mobile ?? '',
    companyName: userDetail.companyName ?? '',
    timezone: userDetail.timezone ?? '',
    countryCode: userDetail.countryCode ?? '',
    profilePicture: teamChat.ProfilePicturePath ?? '',
    logo: teamChat.LogoUrl ?? domainInfo.companyLogo ?? '',
    faviconIcon: domainInfo.faviconIcon ?? '',
    domain: domainInfo.domainName ?? '',
  }
}

/* --------------------------------------------------------------- session */

/** Same localStorage keys the Sales module writes, for cross-app sharing. */
export function storeSsoSession(token: string, user: SsoUserData, deviceId?: string | null): void {
  const config = getSsoConfig()
  const set = (key: string, value: string) => localStorage.setItem(key, value)

  set(config.tokenKey, token)
  set('kit_token', token)
  set('kit_sso_token', token)
  set('Token', token)
  set('kit_userId', user.userId)
  set('userId', user.userId)
  set('kit_parentId', user.parentId)
  set('ParentID', user.parentId)
  set('FName', user.firstName)
  set('LName', user.lastName)
  set('EMail', user.email)
  set('email', user.email)
  set('Mobile', user.mobile)
  set('DisplayName', user.displayName)
  set('LoginName', user.loginName)
  set('kit_loginName', user.loginName)
  set('CompanyName', user.companyName)
  set('ProfilePicturePath', user.profilePicture)
  set('Logo', user.logo)
  set('FaviconIcon', user.faviconIcon)
  set('kit_domain', user.domain)
  set('Timezone', user.timezone)
  set('kit_auth_type', 'sso')
  if (deviceId) set('kit19_device_id', deviceId)
}

export interface SsoSession {
  token: string | null
  userId: number | null
  parentId: number | null
  displayName: string
  loginName: string
  firstName: string
  lastName: string
  email: string
  mobile: string
  companyName: string
  timezone: string
  profilePicture: string
  logo: string
  faviconIcon: string
  domain: string
  isAuthenticated: boolean
  authType: string
}

/** Session reader — tolerant of either the Sales keys or ours. */
export function getSsoSession(): SsoSession {
  const token =
    localStorage.getItem('Token') ||
    localStorage.getItem('kit_token') ||
    localStorage.getItem('kit_sso_token') ||
    localStorage.getItem('kit19_token')
  const userId = Number.parseInt(localStorage.getItem('userId') || localStorage.getItem('kit_userId') || '', 10)
  const parentId = Number.parseInt(
    localStorage.getItem('ParentID') || localStorage.getItem('kit_parentId') || '',
    10,
  )

  return {
    token: token || null,
    userId: Number.isNaN(userId) ? null : userId,
    parentId: Number.isNaN(parentId) ? null : parentId,
    displayName: localStorage.getItem('DisplayName') || '',
    loginName: localStorage.getItem('LoginName') || localStorage.getItem('kit_loginName') || '',
    firstName: localStorage.getItem('FName') || '',
    lastName: localStorage.getItem('LName') || '',
    email: localStorage.getItem('email') || localStorage.getItem('EMail') || '',
    mobile: localStorage.getItem('Mobile') || '',
    companyName: localStorage.getItem('CompanyName') || '',
    timezone: localStorage.getItem('Timezone') || '',
    profilePicture: localStorage.getItem('ProfilePicturePath') || '',
    logo: localStorage.getItem('Logo') || '',
    faviconIcon: localStorage.getItem('FaviconIcon') || '',
    domain: localStorage.getItem('kit_domain') || '',
    isAuthenticated: !!token && !Number.isNaN(userId) && !Number.isNaN(parentId),
    authType: localStorage.getItem('kit_auth_type') || '',
  }
}

/** Clears the session but keeps the shared device id + SSO config blob. */
export function clearSsoSession(): void {
  const deviceId = localStorage.getItem('kit19_device_id')
  const ssoConfig = localStorage.getItem('kit19_sso_config')
  localStorage.clear()
  if (deviceId) localStorage.setItem('kit19_device_id', deviceId)
  if (ssoConfig) localStorage.setItem('kit19_sso_config', ssoConfig)
  sessionStorage.clear()
}

/* ------------------------------------------------------- login / logout */

/** Sends the browser to the SSO login page, restoring `targetPagePath` after. */
export function redirectToSsoLogin(targetPagePath?: string): void {
  const config = getSsoConfig()
  const redirectUri = `${window.location.origin}/callback`
  const cfg = {
    apiBase: config.apiUrl,
    tokenKey: config.tokenKey,
    loginMode: 'desktopapplogin',
    targetPagePath: targetPagePath ?? window.location.pathname + window.location.search,
  }
  const url =
    `${config.loginUrl}?redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&app=${encodeURIComponent(config.appName)}` +
    `&config=${encodeURIComponent(JSON.stringify(cfg))}`
  window.location.href = url
}

/** SSO logout: notify the auth server, clear locally, bounce via the SSO logout page. */
export async function ssoLogout(returnUrl?: string): Promise<void> {
  const config = getSsoConfig()
  const session = getSsoSession()
  const token = session.token
  clearSsoSession()

  if (token) {
    try {
      await fetch(`${config.apiUrl}Authorize/Logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Auth-Key': token },
        body: JSON.stringify({}),
      })
    } catch {
      // Best effort — local session is already gone.
    }
  }

  const back = returnUrl ?? window.location.origin
  window.location.href = `${config.logoutUrl}?redirect_uri=${encodeURIComponent(back)}`
}

/** Strips SSO params from the address bar after a successful callback. */
export function cleanCallbackUrl(): void {
  const url = new URL(window.location.href)
  for (const key of ['token', 'user_id', 'sso', 'config', 'deviceId', 'returnUrl', 'error', 'error_description']) {
    url.searchParams.delete(key)
  }
  url.hash = ''
  window.history.replaceState({}, document.title, url.pathname + url.search)
}
